import { commonClassName } from 'components/pages/PromoScorecard';
import React, { FC, useContext, useEffect, useMemo, useRef } from 'react';
import { CustomSelect } from '../BumpReport/MainSection';
import DownSvg from 'assets/svg/Down';
import { UserContext } from 'contexts/ConfigContext';
import {
    calculateContainerHeight,
    fetchCurrency,
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
    getConfig,
} from 'utils/helperMethods';
import Legend from 'components/molecules/Legend';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { yAxisWidth } from './BarPointChartContainer';
import { salesGraphWidth } from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import MultiLineChart from 'components/molecules/MultiLineChart';
import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import Barchart from 'components/molecules/Barchart';
import { yAxisLabelClass } from '../GrowthAnalysis/ExpandedView';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { FilterContextData } from 'contexts/FilterContext';
import { priceCurveChartDownload } from 'parser/graphToExcelDownloadParser';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PRICE_LADDER_CHART2_DROPDOWN_CHANGED, PRICE_LADDER_CHART3_DROPDOWN_CHANGED } from 'utils/mixpanelEventNames';
import { ConfigKeys } from 'utils/Enums';

type Props = {
    selectedAttributeItem: any;
    selectPPG: any;
    subTileState: any;
    selectedKpi: any;
    multiLineData: any;
    error: any;
    loading: boolean;
    setSelectedKpi: Function;
};

const priceLadderMultiLineId = 'priceLadderMultiLineId';
const chartHeight = calculateContainerHeight(3, 100);
const GroupedLineChartContainer: FC<Props> = ({
    selectedAttributeItem,
    selectPPG,
    subTileState,
    selectedKpi,
    setSelectedKpi,
    multiLineData,
    error,
    loading,
}) => {
    const { appConfig } = useContext(UserContext);
    const priceLadderConfig = getConfig('priceLadder', appConfig);
    const priceCurveConfig = getConfig('priceCurve', appConfig);
    const { decimalForNonPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const graphRef = useRef<any>();
    const { allCheckedAttribute, timeSelectionLabel } = useContext(FilterContextData);

    const updatedChartHeight = chartHeight + 50;

    //graph main axis width
    const containerWidth = useContainerWidth({
        containerId: priceLadderMultiLineId,
        buffer: yAxisWidth * 1 + 34 * 2,
    });
    const perGraphComponentWidth = containerWidth / multiLineData?.xAxisValues?.length || 0;

    //kpi options
    const memoisedKpi = useMemo(() => {
        const kpis = priceLadderConfig?.legendMapping?.[subTileState.key]?.slice(1)?.map((item: any) => {
            return {
                label: item?.label,
                value: item?.label,
                key: item?.dataKey,
                metricKey: item?.metricDataKey,
            };
        });
        return kpis;
    }, [subTileState]);

    const memoisedLegendColors = useMemo(() => {
        const colorMapping: any = {};
        const chartColors = priceCurveConfig?.chartColors;

        //dynamically allocate colors
        const updatedLegendData = multiLineData?.legends?.map((item: any, index: number) => {
            const color = chartColors[index % chartColors?.length];
            colorMapping[item.key] = color;
            return {
                ...item,
                color,
                style: { height: 2, width: 14, borderRadius: 8 },
            };
        });
        return {
            colorMapping,
            legendData: updatedLegendData,
        };
    }, [multiLineData]);

    const currency = fetchCurrency(appConfig) || '';

    const chartExcelData = useMemo(() => {
        return priceCurveChartDownload(
            multiLineData?.chartData,
            multiLineData?.xAxisValues,
            { value: 'value', label: 'value' },
            { value: 'value', label: 'Date' },
        );
    }, [multiLineData]);

    return (
        <div ref={graphRef} className={`${commonClassName} mt-4`}>
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <p className="font-lg text-headerColor font-medium">Price across {selectPPG?.label}</p>
                    <div className="flex justify-between items-center">
                        <h5 className="font-medium font-xs m-0 !text-fontGray mr-3">KPI</h5>
                        <CustomSelect
                            className="dropdown-height dropdown-width-alt uniqSelPricePointRetailerKpiDropDown"
                            suffixIcon={<DownSvg />}
                            value={selectedKpi?.value}
                            disabled={loading}
                            options={memoisedKpi}
                            onChange={(val: any, item: any) => {
                                setSelectedKpi(item);
                                //mixpanel event
                                trackEventMixpanel(PRICE_LADDER_CHART3_DROPDOWN_CHANGED, {
                                    kpiName: item?.label,
                                });
                            }}
                        />
                    </div>
                </div>
                {!loading && (
                    <div className="flex items-center gap-2">
                        <CopyToClipBoard refOfComponent={graphRef} />

                        {!loading && !error && (
                            <DownloadExcel
                                title={'PriceLadderChart3'}
                                excelData={chartExcelData?.data}
                                headers={chartExcelData?.headers}
                            />
                        )}
                    </div>
                )}
            </div>
            {loading ? (
                <div style={{ height: updatedChartHeight }}>
                    <Loader />
                </div>
            ) : error || !multiLineData?.chartData ? (
                <div
                    style={{
                        height: updatedChartHeight,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <ErrorBox msg={String(error || 'No Data')} />
                </div>
            ) : (
                <>
                    <div style={{ overflowX: 'auto', marginBottom: 20 }}>
                        <Legend
                            style={{
                                justifyContent: 'initial',
                                marginInline: 'auto',
                                width: 'fit-content',
                                marginBlock: 4,
                                padding: 4,
                            }}
                            className="legend-size-square flex-none"
                            // itemClassName="flex-none"
                            data={memoisedLegendColors?.legendData}
                        />
                    </div>
                    <p className={`${yAxisLabelClass} mt-2 relative bottom-4 w-[120px]`}>{selectedKpi?.label}</p>
                    <div className="flex">
                        {/* Y AXIS */}
                        <Barchart
                            padding={[0, 0, 18, 0]}
                            barWidth={30}
                            xAxisConfig={false}
                            dataset={[]}
                            height={updatedChartHeight + 50}
                            loading={false}
                            chartContainer={`price-ladder-line-chart-y`}
                            fields={['date', 'value']}
                            //   minBound={scaleMinValue(40, tileChartData?.lineMaxVal)}
                            maxBound={multiLineData?.max}
                            minBound={multiLineData?.min}
                            width={yAxisWidth}
                            colorKey={'type'}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage);
                            }}
                            // Yline
                            YGrid={false}
                        />
                        <div
                            style={{ paddingBottom: 45, overflowX: 'auto', maxWidth: '100%' }}
                            id={priceLadderMultiLineId}
                        >
                            <MultiLineChart
                                xAxisConfig
                                lineStyle={{ opacity: 1, lineWidth: 1.2 }}
                                showLegend
                                padding={[5, 0, 5, 0]}
                                // yAxisConfig={false}
                                dataset={multiLineData?.chartData || []}
                                height={updatedChartHeight + 50}
                                loading={false}
                                chartContainer={`price-ladder-line-chart-main`}
                                fields={['xAxisData', 'value']}
                                maxBound={multiLineData?.max}
                                minBound={multiLineData?.min}
                                width={containerWidth} //subtracting the padding
                                xAxisLabelFormatter={(val: any) => {
                                    return val;
                                }}
                                size={0}
                                colorMapping={memoisedLegendColors?.colorMapping}
                                colorKey={'color'}
                                tooltipFormatter={(xAxis, val) => {
                                    return {
                                        name: xAxis,
                                        value:
                                            currency +
                                            formatNumWithKMBAlt(val, decimalForNonPercentage, decimalForNonPercentage),
                                    };
                                }}
                            />
                            {/* <div className="flex">
                                {multiLineData?.xAxisValues?.map((item: any, index: number) => {
                                    return (
                                        <div
                                            style={{
                                                width: perGraphComponentWidth,
                                                minWidth: perGraphComponentWidth,
                                                overflowWrap: 'anywhere',
                                                fontSize: graphXAxisLabelFontSize,
                                            }}
                                            className="text-center p-[2px]"
                                            key={index}
                                        >
                                            {item}
                                        </div>
                                    );
                                })}
                            </div> */}
                        </div>
                    </div>
                    <p className={`text-center !top-[-42px] mb-[-35px] relative ${yAxisLabelClass}`}>{'Date'}</p>
                </>
            )}
        </div>
    );
};

export default GroupedLineChartContainer;
